import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';

import { LoginEntity, GetAccessListRequest, LogInMode } from '../../models';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { usersService } from 'src/app/services/services/users.service';

@Component({
    selector: 'app-access-logs',
    templateUrl: './access-logs.component.html',
    styleUrls: ['./access-logs.component.scss'],
    standalone: false
})
export class AccessLogsComponent implements OnInit {

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  dataSource = new MatTableDataSource<LoginEntity>([]);
  displayedColumns: string[] = [
    'timeLoggedin',
    'displayName',
    'clientType',
    'operationSystem',
    'webBrowser',
    'ip',
    'logInStatus'
  ];

  loginModes: { value: LogInMode; description: string }[] = [
    { value: LogInMode.All,      description: 'All' },
    { value: LogInMode.Success,  description: 'Success' },
    { value: LogInMode.Rejected, description: 'Rejected' }
  ];

  clientTypes: { value: string; label: string }[] = [
    { value: 'Web',    label: 'Web' },
    { value: 'Mobile', label: 'Mobile' }
  ];

  loginFilters = {
    userName: '',
    clientType: '-1',
    loginMode: LogInMode.All as LogInMode,
    ip: '',
    fromDate: '',
    toDate: ''
  };

  errorMessage = '';
  isLoading = false;

  // NEW: info text like "10 records found"
  maxResultsInfoMessage = '';

  // same concept as in AuditLogsComponent
  minAuditDate: Date | null = new Date(new Date().setMonth(new Date().getMonth() - 3));

  constructor(
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    private usersService: usersService
  ) { }

  ngOnInit(): void {
    this.dataSource.sort = this.sort;

    // initial load (backend will apply its own default if from/to are empty)
    // this.loadLogins();
  }

  // ---------- API CALL ----------
  private loadLogins(): void {
    this.errorMessage = '';
    this.maxResultsInfoMessage = '';
    this.isLoading = true;

    const payload: GetAccessListRequest = {
      userName: this.loginFilters.userName ?? '',
      dteFrom: this.loginFilters.fromDate ?? '',
      dteTo: this.loginFilters.toDate ?? '',
      clientType: this.loginFilters.clientType ?? '',
      ip: this.loginFilters.ip ?? '',
      loginMode: this.loginFilters.loginMode
    };

    this.usersService.GetAccessList(payload).subscribe({
      next: (result) => {
        const allResults = result || [];
        this.dataSource.data = allResults;

        const count = allResults.length;

        // reuse same key as audit, or add a specific one if you prefer
        this.maxResultsInfoMessage = this.i18n.transform(
          'users:audit_recordsFound',
          {
            defaultValue: `${count} records found`,
            count
          }
        );

        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = this.i18n.transform(
          'users:logs_load_error',
          { defaultValue: 'Failed to load access logs.' }
        );
      }
    });
  }

  // ---------- RESET ----------
  onReset(): void {
    this.loginFilters = {
      userName: '',
      clientType: '-1',
      loginMode: LogInMode.All,
      ip: '',
      fromDate: '',
      toDate: ''
    };

    this.maxResultsInfoMessage = '';
    this.loadLogins();
  }

  // ---------- CALENDAR RANGE CHANGE (same pattern as AuditLogs) ----------
  onCalendarRangeChange(range: { dteFrom: Date; dteTo: Date }): void {
    this.loginFilters.fromDate = this.formatDateTime(range.dteFrom);
    this.loginFilters.toDate = this.formatDateTime(range.dteTo);
  }

  // ---------- SEARCH ----------
  onSearchLogin(): void {
    this.errorMessage = '';
    this.maxResultsInfoMessage = '';
    this.loadLogins();
  }

  // ---------- USER AGENT DIALOG ----------
  openUserAgentInfo(info: string): void {
    if (!info) { return; }

    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      width: '600px',
      data: {
        title: this.i18n.transform(
          'users:help_title_userAgent',
          { defaultValue: 'Client details' }
        ),
        text: info,
        renderAsHtml: true
      }
    });
  }

  // ---------- DATE HELPER (same as in AuditLogsComponent) ----------
  private formatDateTime(d: Date): string {
    // dd-MM-yyyy HH:mm
    const dd = ('0' + d.getDate()).slice(-2);
    const mm = ('0' + (d.getMonth() + 1)).slice(-2);
    const yyyy = d.getFullYear();
    const hh = ('0' + d.getHours()).slice(-2);
    const min = ('0' + d.getMinutes()).slice(-2);
    return `${dd}-${mm}-${yyyy} ${hh}:${min}`;
  }
}
